import cx from 'classnames';
import styles from './index.module.scss';
import Image, { StaticImageData } from 'next/image';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ArrowLeft from '../../assets/icon/banner/arrowLeft.svg'
import ArrowRight from '../../assets/icon/banner/arrowRight.svg'
import CommonReactImage from '@/components/CommontReactImage';
import { OpenType } from '@/constants';
import { getOptimizedImageUrl } from '@/utils/image';

/** 图片/视频 枚举 */
export enum AttachType {
  Image = 'image',
  Video = 'video'
}

export interface CarouselItem {
  alt: string;
  /** 区分图片和视频的标记 */
  type: AttachType;
  /** 静态资源或线上获取地址url */
  src: StaticImageData | string;
  /** 视频兜底图 */
  fallbackSrc?: StaticImageData | string;  /** height */
  /** 外链 */
  link?: {
    src: string;
    openType?: OpenType,
  }
}

export interface CarouselProps  {
  /** 业务组件轮播容器宽度 */
  width?: number;
  /** 业务组件轮播容器高度 */
  height?: number;
  /** 轮播时间差 */
  interval?: number;
  /** 轮播内容 */
  items: CarouselItem[];
  /* 轮播图点击埋点上报 */
  onBannerTrackCb?: (order: number, link: string) => void;
}

// 图片/视频轮播组件
  const Carousel = ({ items, interval = 5000, width, height, onBannerTrackCb }: CarouselProps) => {
  // 控制轮播组件自动轮播
  const [autoPlay, setAutoPlay] = useState(true);
  // 鼠标 hover 状态
  const [isHovered, setIsHovered] = useState(false);
  // 当前轮播图片/视频序号
  const [currentIndex, setCurrentIndex] = useState(0);
  // 轮播定时器
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // 当前播放视频的DOM
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // 判断是否有视频正在播放
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  /** 视频refs */
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  /** 兜底图refs */
  const fallbackRefs = useRef<(HTMLImageElement | null)[]>([]);

  useEffect(() => {
    items.forEach((item, index) => {
      if (item.type === AttachType.Video) {
        const video = videoRefs.current[index];
        const fallbackImage = fallbackRefs.current[index];
  
        if (video) {
          video.onerror = () => {
            // 隐藏视频并显示兜底图
            video.style.display = 'none';
            if (fallbackImage) {
              fallbackImage.style.display = 'block';
            }
          };
  
          video.oncanplay = () => {
            // 视频可以播放时，确保显示视频并隐藏兜底图
            video.style.display = 'block';
            if (fallbackImage) {
              fallbackImage.style.display = 'none';
            }
          };
        }
      }
    });
  }, [items]);

  // 自动轮播逻辑
  useEffect(() => {
    if (autoPlay && items.length > 1) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
      }, interval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [autoPlay, items.length, interval]);


  useEffect(() => {
    if (items[currentIndex].type === AttachType.Video) {
      const video = document.getElementById(`video-${currentIndex}`) as HTMLVideoElement;
      if (window.getComputedStyle(video).display === 'none') {
        return;
      }
      videoRef.current = video;
      videoRef.current.play();
      if (items.length === 1) {
        videoRef.current.loop = true;
        return;
      }
      setAutoPlay(false); // 播放视频时暂停自动轮播
      setIsVideoPlaying(true);
      video.onended = () => {
        setAutoPlay(true); // 视频播放结束后恢复自动轮播
        setIsVideoPlaying(false);
        videoRef.current = null;
        handleNextClick();
      };
    }
  }, [currentIndex])


  // 检查是否需要中断播放 （切换下一张/切换上一张）
  const checkPauseVideo = () => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsVideoPlaying(false);
      videoRef.current = null;
      return true;
    }
    return false;
  }

  // 鼠标悬浮
  const handleMouseEnter = () => {
    setAutoPlay(false); // 鼠标悬浮时暂停自动轮播
    setIsHovered(true); // 设置悬浮状态
  };

  // 鼠标移出
  const handleMouseLeave = () => {
    if (!isVideoPlaying) {
      setAutoPlay(true); // 鼠标离开时恢复自动轮播
    }
    setIsHovered(false); // 取消悬浮状态
  };

  // 切换上一张
  const handlePrevClick = () => {
    checkPauseVideo();
    setCurrentIndex((prevIndex) => (prevIndex - 1 + items.length) % items.length);
  };

  // 切换下一张
  const handleNextClick = () => {
    checkPauseVideo();
    setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
  };

  return (
    <div
      style={{width: `${width}px`, height: `${height}px`}}
      className={styles.carousel}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {items.map((item, index) => (
        <div
          key={index}
          className={`${styles.carouselItem} ${index === currentIndex ? styles.active : ''}`}
          onClick={() => {
            const currentItemLink = items[currentIndex].link;
            if (currentItemLink) {
              onBannerTrackCb?.(index, currentItemLink.src);
              window.open(currentItemLink.src, (currentItemLink.openType && currentItemLink.openType === OpenType.NewWindow) ? '_blank' : '_self');
            }
          }}
        >
          {item.type === AttachType.Video ? (
            <>
              <Image
                width={width}
                alt={item.alt}
                height={height}
                className="image"
                draggable={false}
                style={{display: 'none'}}
                loader={({ src }) => src}
                src={getOptimizedImageUrl(item.fallbackSrc as string, 'webp', '2560')}
                ref={(el) => (fallbackRefs.current[index] = el)}
              />
              <video
                muted
                id={`video-${index}`}
                className={styles.video}
                src={item.src as string}
                poster={getOptimizedImageUrl(item.fallbackSrc as string, 'webp', '2560')}
                ref={(el) => (videoRefs.current[index] = el)}
              />
            </>
          ) : (
            <CommonReactImage
              src={item.src as string}
              alt={item.alt}
              disableSkeleton
            />
          )}
        </div>
      ))}

      {/* 仅在 items.length > 1 且鼠标悬浮时显示切换按钮 */}
      {items.length > 1 && isHovered && (
        <>
          <button className={styles.prevButton} onClick={handlePrevClick}><ArrowLeft /></button>
          <button className={styles.nextButton} onClick={handleNextClick}><ArrowRight /></button>
        </>
      )}
      {
        items.length !== 1 && (
          <div className={styles.currentTag}>
            {
              items.map((_, index) => (
                <div key={index} className={cx(styles.tag, currentIndex === index ? styles.activeTag : '')} /> 
              ))
            }
          </div>
        )
      }
    </div>
  );
};

export default Carousel;
